import { Backdrop, Box, CircularProgress, TextField } from '@mui/material';
import List from 'widgets/list/list';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import { ContractsIDList, CurrentPageInCase, LastFetchProgramsList, LoadingProgramsList, ProgramsList } from 'entities/insurance-programs/selector';
import { fetchProgramsList } from 'entities/insurance-programs/actions/fetch-programs-list';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash-es';
import { setCurrentPageInCase } from 'entities/insurance-programs';
import { IStateProps } from 'shared/types/interface/interface';
import { IListProgram } from 'entities/insurance-programs/types/InsurancePrograms-interface';
import SearchIcon from '@mui/icons-material/Search';

import appStyles from 'app/providers/theme/App.module.css';
import CustomButton from 'shared/custom-button/CustomButton';

import styles from 'features/contract-page/ui/components.module.css'
import { isValidIdCase } from 'shared/utils/hooks/useValidationIdCase';
import Loader from 'shared/loader/Loader';

interface InsurancePrograms {
    idContract: string[] | [];
}
export const InsuranceProgramsCase: FC<InsurancePrograms> = ({ idContract }) => {
    const limit = 10;
    const dispatch = useAppDispatch();

    const listProgramsData = useAppSelector(ProgramsList);
    const lastFetch = useAppSelector(LastFetchProgramsList);
    const fetchContractsID = useAppSelector(ContractsIDList, isEqual);

    const isFetching = useRef(false);
    const stableIdContract = useMemo(() => idContract || [], [idContract]);
    const stableSortArray = useMemo(() => [], []);

    const currentPageInCase = useAppSelector(CurrentPageInCase);

    const isLoading = useAppSelector(LoadingProgramsList);

    const currentContracts = stableIdContract;

    const [openSearch, setOpenSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const handleOpenSearch = () => setOpenSearch((prevState) => !prevState);

    const oneDay = 24 * 60 * 60 * 1000;

    const shouldFetch = () => {
        const isSameParams = isEqual(currentContracts, fetchContractsID);
        const isDataStale = !lastFetch || (Date.now() - lastFetch > oneDay);

        return !isSameParams || isDataStale;
    };

    const [isValid, setIsValid] = useState(true);

    const fetchTasksWithValidation = (idContract: string[]) => {
        if (idContract.every(isValidIdCase)) {
            dispatch(fetchProgramsList({
                contractID: idContract,
                sortArray: stableSortArray
            }));
        } else {
            setIsValid(false);
            console.error('Invalid idCase');
        }
    };

    useEffect(() => {
        if (isFetching.current) return;
        if (shouldFetch()) {
            isFetching.current = true;
            new Promise<void>((resolve) => {
                fetchTasksWithValidation(currentContracts);
                resolve();
            }).finally(() => {
                isFetching.current = false;
            });
        }
    }, [
        dispatch,
        stableIdContract,
        lastFetch,
        fetchContractsID,
    ]);

    const handleChildAction = useCallback((page: number) => {
        dispatch(setCurrentPageInCase({ page }));
    }, [dispatch]);


    const state: IStateProps = {
        fromInsuranceContractPage: true,
        id: idContract?.[0],
        activeTab: 3
    };
    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        dispatch(setCurrentPageInCase({ page: 1 }));
    }, [dispatch]);

    
    const filtered = (() => {
        let filteredData = [...listProgramsData];

        if (searchQuery) {
            filteredData = filteredData.filter((item: IListProgram) =>
                item.PrintName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.InsurerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.ContractNumber.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        return filteredData;
    })();

    const paginatedProgram = () => {
        const startIndex = (currentPageInCase - 1) * limit;
        return filtered.slice(startIndex, startIndex + limit);
    };

    const handleUpdate = () => {
        isFetching.current = true;
        fetchTasksWithValidation(currentContracts);
        isFetching.current = false;
    }

    if (!isValid) {
        return (
            <p className={styles.errorText}>Некорректный ID договора</p>
        )
    }
    return (
        <>
            {isLoading && (
                <Loader
                    title='Загрузка...'
                />
            )}
            {!isLoading && (
                <>
                    <Box className={appStyles.iconSort}>
                        <span onClick={handleOpenSearch} className={`${openSearch ? appStyles.iconSelected : ''}`}><SearchIcon /></span>
                    </Box>
                    {openSearch && (
                        <TextField
                            label="Поиск"
                            variant="outlined"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            fullWidth
                            margin="normal"
                        />
                    )}
                    {listProgramsData.length > 0 && filtered.length > 0 ? (
                        <>
                            <Box sx={{
                                width: '100%',
                                overflow: 'hidden',
                                position: 'relative'
                            }}>
                                <List
                                    title={['Программа страхования', 'Номер договора', 'Начало действия', 'Окончание действия', 'Количество застрахованных', 'Страхователь']}
                                    data={paginatedProgram()}
                                    renderItem={(item: any) => (
                                        <>
                                            <Box><p>{item.PrintName}</p></Box>
                                            <Box><p>{item.ContractNumber}</p></Box>
                                            <Box><p>{item.StartDate}</p></Box>
                                            <Box><p>{item.EndDate}</p></Box>
                                            <Box><p>{item.InsuredQuantity}</p></Box>
                                            <Box><p>{item.InsurerName}</p></Box>
                                        </>
                                    )}
                                    renderPagination={filtered.length > 10}
                                    pageCount={Math.ceil(filtered.length / 10)}
                                    onAction={handleChildAction}
                                    currentPage={currentPageInCase}
                                    pathnameProps="/insurance-programs/case/"
                                    stateProps={state}
                                    link={true}
                                />
                                <Box className={styles.updateBtnBox}>
                                    <CustomButton
                                        handleClick={handleUpdate}
                                        title='Обновить данные'
                                        textColor="#0054E6"
                                        backgroundColor="transparent"
                                        padding="5px 30px"
                                        gap="10px"
                                        border='none'
                                    />
                                </Box>

                            </Box>
                        </>


                    ) : (
                        <>
                            <Box className={appStyles.infoBox}>
                                <p>Программы страхования не найдены</p>
                            </Box>
                            <Box className={styles.updateBtnBox}>
                                <CustomButton
                                    handleClick={handleUpdate}
                                    title='Обновить данные'
                                    textColor="#0054E6"
                                    backgroundColor="transparent"
                                    padding="5px 30px"
                                    gap="10px"
                                    border='none'
                                />
                            </Box>
                        </>
                    )}
                </>
            )}

        </>

    );
};

export default InsuranceProgramsCase;